import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { load } from '@cashfreepayments/cashfree-js';
import { AlertTriangle, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import './RetryPayment.css';

/**
 * Where the "complete your payment" link in the payment-failed email/WhatsApp
 * message leads. This route sits behind PrivateRoutes, so an unauthenticated
 * visitor is bounced to /login first and lands back here automatically after
 * signing in (see PrivateRoutes.jsx / Login.jsx's existing redirect-after-
 * login handling) -- no separate "remember what I was doing" logic needed
 * here, that part already exists for every private route.
 *
 * On mount this asks the backend for a FRESH Cashfree payment session (the
 * old one from the failed attempt is dead -- Cashfree sessions don't survive
 * being reused) and launches checkout immediately, so there's no second click
 * required once someone's actually here.
 */
const RetryPayment = () => {
  const { orderInfoId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('starting'); // starting | launching | error
  const [errorMessage, setErrorMessage] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // StrictMode/dev double-invoke guard
    startedRef.current = true;

    const start = async () => {
      try {
        const response = await fetch(
          `https://riymay.com/api/v1/user/orders/${orderInfoId}/retry-payment`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            },
          }
        );
        const data = await response.json();

        if (data.message === 'Invalid token' || data.message === 'Invalid or expired token') {
          localStorage.removeItem('userToken');
          navigate('/login', { state: { from: { pathname: `/retry-payment/${orderInfoId}` } } });
          return;
        }

        if (!response.ok || !data.cashfreeOrder || !data.cashfreeOrder.payment_session_id) {
          setStatus('error');
          setErrorMessage(data.message || 'Could not start a new payment for this order.');
          return;
        }

        setStatus('launching');
        const cashfree = await load({ mode: 'production' });
        const result = await cashfree.checkout({
          paymentSessionId: data.cashfreeOrder.payment_session_id,
          redirectTarget: '_self',
          returnUrl: 'https://riymay.com/payment/status',
        });

        if (result.error) {
          toast.error(`Payment failed: ${result.error.message || 'Unknown error'}`);
          setStatus('error');
          setErrorMessage(result.error.message || 'The payment attempt did not go through.');
        }
        // On success Cashfree itself redirects to returnUrl per redirectTarget:
        // "_self" -- /payment/status already handles reading the real result
        // back from the webhook, so nothing further is done here.
      } catch (err) {
        console.error('[RetryPayment] Failed to start retry checkout:', err);
        setStatus('error');
        setErrorMessage('Something went wrong starting your payment. Please try again.');
      }
    };

    start();
  }, [orderInfoId, navigate]);

  return (
    <div className="retry-payment-page">
      {status !== 'error' ? (
        <div className="retry-payment-loading">
          <Loader className="retry-payment-spinner" size={32} />
          <p>{status === 'launching' ? 'Opening secure checkout…' : 'Preparing your payment…'}</p>
        </div>
      ) : (
        <div className="retry-payment-error">
          <AlertTriangle size={32} />
          <p>{errorMessage}</p>
          <button onClick={() => window.location.reload()}>Try again</button>
        </div>
      )}
    </div>
  );
};

export default RetryPayment;
